import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApplicationPackage} from 'src/app/enums/application-package.enum';
import {Service} from 'src/app/enums/service.enum';
import {CustomHttpUrlEncodingCodec} from 'src/app/main/encoder';
import {Category} from 'src/app/model/cms-vod-core/category.model';
import {PageOfCategories} from 'src/app/model/page-of-categories.model';
import {environment} from 'src/environments/environment';
import {BulkCategoryInput} from '../../../model/bulk-category-input.model';

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  readonly categoryApiUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.CATEGORY;

  constructor(private http: HttpClient) {}

  // Get All Categories
  getAllCategories(page?: number, platform?: string, menuItemId?: number, name?: String, visibility?: boolean, size?: number, menuItemIds?: number[], sortParam?: string[]): Observable<PageOfCategories> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    page = page - 1;
    if (page >= 0) {
      queryParams = queryParams.set('page', <any>page);
    }
    if (platform) {
      queryParams = queryParams.set('platform', <any>platform);
    }
    if (menuItemId) {
      queryParams = queryParams.set('menuItemId', <any>menuItemId);
    }
    if (menuItemIds) {
      queryParams = queryParams.set('menuItemIds', <any>menuItemIds);
    }
    if (name) {
      queryParams = queryParams.set('name', <any>name);
    }
    if (visibility != null) {
      queryParams = queryParams.set('visibility', <any>visibility);
    }
    if (size) {
      queryParams = queryParams.set('itemCount', <any>size);
    }
    if (sortParam && sortParam.length) {
      sortParam.forEach((s) => {
        queryParams = queryParams.append('sortParam', <any>s);
      });
    }
    return this.http.get<PageOfCategories>(this.categoryApiUrl, {params: queryParams});
  }
  getCategoryById(id: number): Observable<Category> {
    return this.http.get<Category>(this.categoryApiUrl + `/${id}`);
  }

  getCategoryByExternalId(externalId: string, silent?: boolean): Observable<Category> {
    let options: { headers?: HttpHeaders } = {};
    if (silent) {
      options.headers = new HttpHeaders().set('X-Skip-Error-Toast', 'true');
    }
    return this.http.get<Category>(this.categoryApiUrl + `/external/${externalId}`, options);
  }

  createCategory(category: Category): Observable<Category> {
    return this.http.post<Category>(this.categoryApiUrl, category, {responseType: 'text' as 'json'});
  }

  updateCategory(category: Category): Observable<Category> {
    return this.http.put<Category>(this.categoryApiUrl, category);
  }

  updateCategoriesPriority(bulkCategoryInput: BulkCategoryInput): Observable<BulkCategoryInput> {
    return this.http.put<BulkCategoryInput>(this.categoryApiUrl + `/priority`, bulkCategoryInput, {responseType: 'text' as 'json'});
  }

  setCategoryPoster(categoryId: number, posterDefinitionId: number, file: File, autoPosterCreate: boolean): Observable<any> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (autoPosterCreate != undefined) {
      queryParams = queryParams.set('autoPosterCreate', <any>autoPosterCreate);
    }
    let formData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(this.categoryApiUrl + `/${categoryId}/posters/${posterDefinitionId}/upload`, formData, {
      params: queryParams,
    });
  }

  uploadCategoryIcon(categoryId: number, file: File): Observable<string> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http.post<string>(`${this.categoryApiUrl}/${categoryId}/icon/upload`, fd, { responseType: 'text' as 'json' });
  }

  removeCategoryIcon(categoryId: number): Observable<string> {
    return this.http.delete<string>(`${this.categoryApiUrl}/${categoryId}/icon`, { responseType: 'text' as 'json' });
  }

  // Kategori görselleri (background / poster): ikonla aynı yapı, poster tanımı gerekmez.
  uploadCategoryImage(categoryId: number, slot: string, file: File): Observable<string> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http.post<string>(
      `${this.categoryApiUrl}/${categoryId}/images/${slot}/upload`, fd, { responseType: 'text' as 'json' });
  }

  removeCategoryImage(categoryId: number, slot: string): Observable<string> {
    return this.http.delete<string>(
      `${this.categoryApiUrl}/${categoryId}/images/${slot}`, { responseType: 'text' as 'json' });
  }

  removePoster(categoryId: number, posterDefinitionId: number): Observable<string> {
    return this.http.delete<string>(this.categoryApiUrl + `/${categoryId}/posters/${posterDefinitionId}`, {responseType: 'text' as 'json'});
  }

  deleteCategory(categoryId: number): Observable<number> {
    return this.http.delete<number>(this.categoryApiUrl + `/${categoryId}`);
  }

  updateCategoryVisibility(category: Category): Observable<string> {
    let visibilityString!: string;

    if (category.visibility) {
      visibilityString = 'enable';
    } else {
      visibilityString = 'disable';
    }

    return this.http.put<string>(this.categoryApiUrl + `/${category.id}/visibility/${visibilityString}`, null, {responseType: 'text' as 'json'});
  }

  getAllActiveCategoryAndLabels(): Observable<string[]> {
    return this.http.get<string[]>(`${this.categoryApiUrl}/getAllActiveCategoryAndLabels`);
  }

  deleteByPlatform(name: string): Observable<any> {
    return this.http.delete<any>(this.categoryApiUrl + `/by-platform/${name}`, {responseType: 'text' as 'json'});
  }

  getCategoriesForFilter(): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.categoryApiUrl}/getCategoriesForFilter`);
  }

  getRoots(platform?: string): Observable<Category[]> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (platform) {
      queryParams = queryParams.set('platform', platform);
    }
    return this.http.get<Category[]>(`${this.categoryApiUrl}/roots`, {params: queryParams});
  }

  getChildren(id: number): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.categoryApiUrl}/${id}/children`);
  }

  getTree(rootId?: number): Observable<Category[]> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (rootId != null) {
      queryParams = queryParams.set('rootId', String(rootId));
    }
    return this.http.get<Category[]>(`${this.categoryApiUrl}/tree`, {params: queryParams});
  }

  getHomeReferenceTargets(): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.categoryApiUrl}/home-reference-targets`);
  }

  // Banner'da Target Type = Vod Bundle seçildiğinde Target UId listesini besler.
  // Gizli (visibility=false) bundle'lar da döner — seçilebilir kalmaları özelliğin amacı.
  getVodBundleTargets(): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.categoryApiUrl}/vod-bundles`);
  }

  getCategoryPath(id: number): Observable<string> {
    return this.http.get<string>(`${this.categoryApiUrl}/${id}/path`, {responseType: 'text' as 'json'});
  }

  moveCategory(id: number, newParentId?: number): Observable<Category> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (newParentId != null) {
      queryParams = queryParams.set('newParentId', String(newParentId));
    }
    return this.http.put<Category>(`${this.categoryApiUrl}/${id}/move`, null, {params: queryParams});
  }

  searchTree(q: string): Observable<Category[]> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    queryParams = queryParams.set('q', q);
    return this.http.get<Category[]>(`${this.categoryApiUrl}/search-tree`, {params: queryParams});
  }

  setPlatformVisibility(id: number, platformVisibility: {[key: string]: boolean}): Observable<string> {
    return this.http.put<string>(`${this.categoryApiUrl}/${id}/platform-visibility`, platformVisibility, {responseType: 'text' as 'json'});
  }

  getCategoryContents(categoryId: number, page?: number, itemCount?: number, name?: string, contentType?: string, platform?: string): Observable<any> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (page != null) queryParams = queryParams.set('page', String(page));
    if (itemCount != null) queryParams = queryParams.set('itemCount', String(itemCount));
    if (name) queryParams = queryParams.set('name', name);
    if (contentType) queryParams = queryParams.set('contentType', contentType);
    if (platform) queryParams = queryParams.set('platform', platform);
    return this.http.get<any>(`${this.categoryApiUrl}/${categoryId}/contents`, {params: queryParams});
  }

  updateCategoryContentOrder(categoryId: number, orderedItems: {id: number; contentType: string}[]): Observable<any> {
    return this.http.put<any>(`${this.categoryApiUrl}/${categoryId}/contents/order`, orderedItems, {responseType: 'text' as 'json'});
  }

  applyStaticAutoFill(categoryId: number): Observable<any> {
    return this.http.post<any>(`${this.categoryApiUrl}/${categoryId}/contents/auto-fill`, null, {responseType: 'text' as 'json'});
  }

  getAvailableContents(categoryId: number, page?: number, itemCount?: number, name?: string, contentType?: string): Observable<any> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (page != null) queryParams = queryParams.set('page', String(page));
    if (itemCount != null) queryParams = queryParams.set('itemCount', String(itemCount));
    if (name) queryParams = queryParams.set('name', name);
    if (contentType) queryParams = queryParams.set('contentType', contentType);
    return this.http.get<any>(`${this.categoryApiUrl}/${categoryId}/available-contents`, {params: queryParams});
  }

  addCategoryContent(categoryId: number, contentId: number, contentType?: string, startTime?: number, endTime?: number, contentGroupIds?: number[]): Observable<any> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (contentType) queryParams = queryParams.set('contentType', contentType);
    if (startTime != null) queryParams = queryParams.set('startTime', String(startTime));
    if (endTime != null) queryParams = queryParams.set('endTime', String(endTime));
    if (contentGroupIds && contentGroupIds.length) queryParams = queryParams.set('contentGroupIds', contentGroupIds.join(','));
    return this.http.post<any>(`${this.categoryApiUrl}/${categoryId}/contents/${contentId}`, null, {params: queryParams, responseType: 'text' as 'json'});
  }

  removeCategoryContent(categoryId: number, contentId: number, contentType?: string): Observable<any> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (contentType) queryParams = queryParams.set('contentType', contentType);
    const headers = new HttpHeaders().set('X-Skip-Error-Toast', 'true');
    return this.http.delete<any>(`${this.categoryApiUrl}/${categoryId}/contents/${contentId}`, {
      headers,
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  // Dynamic categories (OQ-03): no dedicated backend filter endpoint exists,
  // so we reuse /tree (all categories, flattened) and filter client-side for
  // kind !== 'STATIC'. Used by banner-management's Dynamic Labels dropdown.
  getDynamicCategories(platform?: string): Observable<Category[]> {
    return new Observable<Category[]>((subscriber) => {
      this.getTree().subscribe({
        next: (categories) => {
          const dynamic = (categories || []).filter((c) => {
            if (!c.kind || c.kind === 'STATIC') return false;
            if (!platform) return true;
            const visibility = (c as any).platformVisibility;
            return !visibility || visibility[platform] !== false;
          });
          subscriber.next(dynamic);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });
    });
  }
}
